import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Form,
  Button,
  Select,
  DatePicker,
  message,
  Space,
  Card,
  Row,
  Col,
  InputNumber,
  FormInstance,
  Typography,
} from "antd";
import { supabase } from "../../../../lib/supabase";
import { trackFeatureUsage, captureError } from "../../../../lib/posthog";
import {
  Tariff,
  TransportType,
  Port,
  TariffBracket,
  ContainerSize,
  BookingType,
  TariffFormValues,
} from "../utils/types";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;

export interface TariffFormProps {
  mode: "create" | "edit" | "duplicate";
  tariff?: Tariff;
  initialValues?: Partial<TariffFormValues>;
  transportTypes: TransportType[];
  originPorts: Port[];
  destinationPorts: Port[];
  tariffBrackets: TariffBracket[];
  containerSizes: ContainerSize[];
  bookingTypes: BookingType[];
  onSuccess: () => void;
  onCancel: () => void;
  formRef?: (form: FormInstance) => void;
}

const TariffForm: React.FC<TariffFormProps> = ({
  mode,
  tariff,
  initialValues,
  transportTypes,
  originPorts,
  destinationPorts,
  tariffBrackets = [],
  containerSizes,
  bookingTypes,
  onSuccess,
  onCancel,
  formRef,
}) => {
  const [form] = Form.useForm();
  const [availableBrackets, setAvailableBrackets] = useState<TariffBracket[]>(
    []
  );
  const [submitting, setSubmitting] = useState(false);

  // Track if airfreight is selected
  const [isAirfreight, setIsAirfreight] = useState<boolean>(false);

  // Track if seafreight (ID 2) is selected
  const [isSeafreight, setIsSeafreight] = useState<boolean>(false);

  // Track selected booking type
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState<
    number | null
  >(null);

  // Track if FCL booking type is selected (ID 1)
  const [isFCL, setIsFCL] = useState<boolean>(false);

  // Use refs to track initialization state
  const didMount = useRef(false);
  const formRefSet = useRef(false);

  // Set form reference only once on mount
  useEffect(() => {
    if (formRef && !formRefSet.current) {
      formRef(form);
      formRefSet.current = true;
    }

    didMount.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize form values from props
  useEffect(() => {
    // Reset form first to clear any previous values
    form.resetFields();

    let transportTypeValue = null;
    let bookingsTypeValue = null;

    if (mode === "edit" && tariff) {
      transportTypeValue = tariff.transport_type;
      bookingsTypeValue = tariff.bookings_type;

      const formValues = {
        from: tariff.from ? dayjs(tariff.from) : null,
        to: tariff.to ? dayjs(tariff.to) : null,
        transport_type: tariff.transport_type,
        price: tariff.price,
        origin_port: tariff.origin_port,
        destination_port: tariff.destination_port,
        tariff_bracket: tariff.tariff_bracket,
        container_size: tariff.container_size,
        bookings_type: tariff.bookings_type,
      };

      // Use setTimeout to ensure the reset happens first
      setTimeout(() => {
        form.setFieldsValue(formValues);
      }, 0);
    } else if (mode === "duplicate" && tariff) {
      transportTypeValue = tariff.transport_type;
      bookingsTypeValue = tariff.bookings_type;

      const formValues = {
        from: tariff.from ? dayjs(tariff.from) : null,
        to: tariff.to ? dayjs(tariff.to) : null,
        transport_type: tariff.transport_type,
        price: tariff.price,
        origin_port: tariff.origin_port,
        destination_port: tariff.destination_port,
        tariff_bracket: tariff.tariff_bracket,
        container_size: tariff.container_size,
        bookings_type: tariff.bookings_type,
      };

      setTimeout(() => {
        form.setFieldsValue(formValues);
      }, 0);
    } else if (initialValues) {
      transportTypeValue = initialValues.transport_type;
      bookingsTypeValue = initialValues.bookings_type;

      const formValues = {
        ...initialValues,
        from: initialValues.from ? dayjs(initialValues.from) : null,
        to: initialValues.to ? dayjs(initialValues.to) : null,
      };

      setTimeout(() => {
        form.setFieldsValue(formValues);
      }, 0);
    }

    // Initialize available brackets if transport type is set
    if (transportTypeValue) {
      const filtered =
        tariffBrackets.filter(
          (bracket) => bracket.transport_type === transportTypeValue
        ) || [];
      setAvailableBrackets(filtered);

      // Check if transport type is seafreight (ID 2)
      if (transportTypeValue === 2) {
        setIsSeafreight(true);
      } else {
        setIsSeafreight(false);
      }

      // Check if airfreight is selected
      checkIfAirfreight(transportTypeValue);
    }

    // Set the selected booking type and FCL flag
    if (bookingsTypeValue) {
      setSelectedBookingTypeId(bookingsTypeValue);
      setIsFCL(bookingsTypeValue === 1); // Set FCL flag if booking type is 1 (FCL)
    }
  }, [mode, tariff, initialValues, form]);

  // Check if the selected transport type is airfreight
  const checkIfAirfreight = useCallback(
    (transportTypeId: number | null) => {
      if (!transportTypeId) {
        setIsAirfreight(false);
        return;
      }

      // Remove the seafreight check since we handle it elsewhere
      const selectedTransportType = transportTypes.find(
        (type) => type.id === transportTypeId
      );
      const isAir =
        selectedTransportType?.name?.toLowerCase().includes("air") || false;
      setIsAirfreight(isAir);

      // If switching to airfreight, clear container size and booking type
      if (isAir) {
        form.setFieldsValue({
          container_size: null,
          bookings_type: null,
        });
      }
    },
    [transportTypes, form]
  );

  // Memoize the transport type change handler
  const handleTransportTypeChange = useCallback(
    (value: number) => {
      // Filter brackets based on selected transport type
      if (value) {
        const filtered =
          tariffBrackets?.filter(
            (bracket) => bracket.transport_type === value
          ) || [];
        setAvailableBrackets(filtered);
        form.setFieldsValue({ tariff_bracket: null });

        // Set the seafreight flag if transport type is 2
        setIsSeafreight(value === 2);

        // Check if airfreight is selected and update state
        checkIfAirfreight(value);
      } else {
        setAvailableBrackets([]);
        form.setFieldsValue({ tariff_bracket: null });
        setIsAirfreight(false);
        setIsSeafreight(false);
      }
    },
    [tariffBrackets, form, checkIfAirfreight]
  );

  // Update the booking type change handler
  const handleBookingTypeChange = useCallback(
    (value: number | null) => {
      setSelectedBookingTypeId(value);

      // Set FCL flag if booking type is FCL (ID 1)
      setIsFCL(value === 1);

      // If booking type is cleared, clear container size and tariff bracket
      if (value === null || value === undefined) {
        form.setFieldsValue({
          container_size: null,
          tariff_bracket: null,
        });
      }

      // For FCL (ID 1), clear container size but keep tariff bracket
      else if (value === 1) {
        form.setFieldsValue({ container_size: null });
      }
      // For LCL (ID 2) or other booking types, clear tariff bracket
      else {
        form.setFieldsValue({ tariff_bracket: null });
      }
    },
    [form]
  );

  const handleFinish = async () => {
    setSubmitting(true);

    // Get fresh values directly from form instance to avoid stale data
    const freshValues = form.getFieldsValue();

    try {
      // Use fresh values instead of the parameter values
      const {
        from,
        to,
        transport_type,
        price,
        origin_port,
        destination_port,
        tariff_bracket,
        container_size,
        bookings_type,
      } = freshValues;

      const formattedFrom = from?.format("YYYY-MM-DD") || null;
      const formattedTo = to?.format("YYYY-MM-DD") || null;

      let result;

      if (mode === "create") {
        // Create new tariff
        result = await supabase
          .from("tariffs")
          .insert({
            from: formattedFrom,
            to: formattedTo,
            transport_type,
            price,
            origin_port,
            destination_port,
            tariff_bracket,
            container_size,
            bookings_type,
          })
          .select();

        trackFeatureUsage("tariff_created");
      } else if (mode === "edit" && tariff) {
        // Update existing tariff
        result = await supabase
          .from("tariffs")
          .update({
            from: formattedFrom,
            to: formattedTo,
            transport_type,
            price,
            origin_port,
            destination_port,
            tariff_bracket,
            container_size,
            bookings_type,
          })
          .eq("id", tariff.id)
          .select();

        trackFeatureUsage("tariff_updated");
      } else if (mode === "duplicate") {
        // Create duplicate tariff
        result = await supabase
          .from("tariffs")
          .insert({
            from: formattedFrom,
            to: formattedTo,
            transport_type,
            price,
            origin_port,
            destination_port,
            tariff_bracket,
            container_size,
            bookings_type,
          })
          .select();

        trackFeatureUsage("tariff_duplicated");
      }

      const { error } = result || { error: new Error("Unknown error") };

      if (error) {
        throw error;
      }

      message.success(
        mode === "create"
          ? "Tariff created successfully!"
          : mode === "edit"
          ? "Tariff updated successfully!"
          : "Tariff duplicated successfully!"
      );

      onSuccess();
    } catch (error) {
      console.error(
        `Error ${mode === "create" ? "creating" : "updating"} tariff:`,
        error
      );
      message.error(
        `Failed to ${
          mode === "create" ? "create" : "update"
        } tariff. Please try again.`
      );
      captureError(error, {
        source: "TariffForm",
        action: `handle${mode.charAt(0).toUpperCase() + mode.slice(1)}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title={
        mode === "create"
          ? "Create New Tariff"
          : mode === "edit"
          ? "Edit Tariff"
          : "Duplicate Tariff"
      }
      bordered={false}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {/* First row - dates and price */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="from"
              label="Valid From"
              rules={[{ required: true, message: "Please select start date" }]}
            >
              <DatePicker style={{ width: "100%" }} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="to"
              label="Valid To"
              rules={[{ required: true, message: "Please select end date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="price"
              label="Price"
              rules={[{ required: true, message: "Please enter price" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                precision={2}
                min={0}
                prefix="€"
                placeholder="Enter price"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Second row - transport type, origin and destination */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="transport_type"
              label="Transport Type"
              rules={[
                { required: true, message: "Please select transport type" },
              ]}
            >
              <Select
                placeholder="Select Transport Type"
                onChange={handleTransportTypeChange}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {transportTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="origin_port"
              label="Origin Port"
              rules={[{ required: true, message: "Please select origin port" }]}
            >
              <Select
                placeholder="Select Origin Port"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {originPorts.map((port) => (
                  <Option key={port.id} value={port.id}>
                    {port.name} ({port.country})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="destination_port"
              label="Destination Port"
              rules={[
                { required: true, message: "Please select destination port" },
              ]}
            >
              <Select
                placeholder="Select Destination Port"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {destinationPorts.map((port) => (
                  <Option key={port.id} value={port.id}>
                    {port.name} ({port.country})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Third row - brackets and container sizes */}
        <Row gutter={[16, 16]}>
          {/* Booking Type - Only shown when transport type is selected and not airfreight */}
          {!isAirfreight &&
            form.getFieldValue("transport_type") !== null &&
            form.getFieldValue("transport_type") !== undefined && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="bookings_type"
                  label="Booking Type"
                  help=""
                  rules={[
                    { required: true, message: "Please select booking type" },
                  ]}
                >
                  <Select
                    placeholder="Select Booking Type"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={handleBookingTypeChange}
                  >
                    {bookingTypes && bookingTypes.length > 0 ? (
                      bookingTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>No booking types available</Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            )}

          {/* Container Size - Only shown for LCL booking types and when transport type is selected and not airfreight */}
          {!isAirfreight &&
            selectedBookingTypeId === 2 &&
            form.getFieldValue("transport_type") !== null &&
            form.getFieldValue("transport_type") !== undefined && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="container_size"
                  label="Container Size"
                  help="Container size for LCL bookings"
                  rules={[
                    { required: true, message: "Please select container size" },
                  ]}
                >
                  <Select
                    placeholder="Select Container Size"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {containerSizes && containerSizes.length > 0 ? (
                      containerSizes.map((size) => (
                        <Option key={size.id} value={size.id}>
                          {size.name}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>No container sizes available</Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            )}

          {/* Tariff Bracket - Only shown when transport type is selected and visible for FCL bookings */}
          {form.getFieldValue("transport_type") !== null &&
            form.getFieldValue("transport_type") !== undefined && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="tariff_bracket"
                  label="Tariff Bracket"
                  help={
                    selectedBookingTypeId === 2
                      ? "Tariff bracket is not applicable for LCL bookings"
                      : isFCL
                      ? "Weight bracket for FCL shipments"
                      : availableBrackets.length === 0
                      ? "Select a transport type first to enable this field"
                      : ""
                  }
                  rules={[
                    {
                      required:
                        isSeafreight && isFCL && selectedBookingTypeId === 1,
                      message: "Please select tariff bracket",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Tariff Bracket"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={
                      availableBrackets.length === 0 ||
                      !isSeafreight ||
                      selectedBookingTypeId === 2 ||
                      !isFCL
                    }
                  >
                    {availableBrackets.map((bracket) => (
                      <Option key={bracket.id} value={bracket.id}>
                        {bracket.from} - {bracket.to}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

          {/* Add note for airfreight tariffs */}
          {isAirfreight && (
            <Col xs={24} sm={24} md={16}>
              <div
                style={{
                  padding: "8px 12px",
                  background: "#f6f6f6",
                  borderRadius: "4px",
                }}
              >
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                <Text type="secondary">
                  For airfreight tariffs, container size and booking type are
                  not applicable and have been hidden.
                </Text>
              </div>
            </Col>
          )}
        </Row>

        {/* Form actions */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {mode === "create"
                ? "Create Tariff"
                : mode === "edit"
                ? "Update Tariff"
                : "Duplicate Tariff"}
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TariffForm;
